import { getAccessTokenSilently } from "utils/functions";
import CircularProgress from "@mui/material/CircularProgress";
import { ReactComponent as Close } from "assets/icons/close.svg";
import BottomArrowSvg from "assets/icons/svg/BottomArrowSvg";

import {
  HintField,
  InputField,
  LabelField,
  MyRadioGroup,
  Text,
} from "components";
import { AsyncAutoCompleteField } from "components/autoCompleteField/AsyncAutoField";
import Button from "components/button/Button";
import SwitchComp from "components/switch";
import { Formik } from "formik";
import { useDisableEnterKey } from "hook";
import { useSearch } from "hook/useSearch";
import AuthService from "Lib/API/Auth/index";
import MemberService from "Lib/API/Member";
import React, { useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import Select, { components } from "react-select";
import { emailValidation } from "utils/validation";
import { deceasedOptions } from "./deceasedOptions";
import { AddMournerContainer, CloseButton } from "./Theme";
import { FontColors } from "theme/fontConfig";

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <BottomArrowSvg label="Emoji" />
    </components.DropdownIndicator>
  );
};
const formatOptionLabel = ({ label, account_id }) => {
  return (
    <div className="dropdown-container" key={account_id}>
      <div className="icon-name-container d-flex align-items-center justify-content-start">
        <div className="name-container" style={{ marginInlineStart: 10 }}>
          <div>{label}</div>
        </div>
      </div>
    </div>
  );
};
const selectControlStyles = {
  backgroundColor: "#F3F6F9",
  borderColor: "#F3F6F9",
  color: FontColors.Slate3F4254,
  transition:
    "color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
  paddingTop: "6px",
  paddingBottom: "6px",
  cursor: "pointer",
};
const colourStyles = {
  control: (styles) => ({
    ...styles,
    ...selectControlStyles,
  }),
  menuList: (styles) => ({
    ...styles,
    maxHeight: "200px",
  }),
};

const AddMourner = ({
  deceasedId,
  onAddSuccess,
  closeModal,
  selectedMourners,
  selectedMourner,
  type = "create",
}) => {
  const deceasedRelationship = localStorage.getItem("deceasedRelationship")
    ? JSON.parse(localStorage.getItem("deceasedRelationship"))
    : [];
  const deceasedRelationshipsOptions = deceasedRelationship || deceasedOptions;

  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addingMournerLoading, setAddingMournerLoading] = useState(false);
  const [search, setSearch] = useState(undefined);
  const [searchSource, setSearchSource] = useState("");
  const [contactsList, setContactsList] = useState([]);
  const { searchAction } = useSearch({ action: setSearch });

  useEffect(() => {
    async function fetchMyAPI() {
      setLoading(true);
      const token = await getAccessTokenSilently();
      await MemberService.getContactsList(token, search)
        .then((res) => {
          if (res?.contacts?.length > 0) {
            //if res.contact = [] will add the search value to guest email address
            res.contacts.map((item) => {
              item.profile_img = item?.profile_img;
              item.id = item.member_id;
              item.label = item?.first_name + " " + item?.last_name;
              item.name = item?.first_name + " " + item?.last_name;
              item.value = item._id;
              return item;
            });
            setContactsList(res?.contacts);
          } else {
            if (searchSource === "account") {
              setEmail(search);
            }
          }
          setLoading(false);
        })
        .catch((err) => {
          toastr.error("Error", err?.data?.message);
        });
    }
    if (search !== "" && search !== undefined && search.length > 2) {
      fetchMyAPI();
    }
  }, [search]);

  useDisableEnterKey(true);

  const getSelectedMournerRelation = () => {
    const selectedRelation = deceasedRelationshipsOptions.find(
      (option) => option.value === selectedMourner?.relation
    );
    return selectedRelation?.value
      ? {
          id: selectedRelation?.value,
          name: selectedRelation?.label,
        }
      : "";
  };

  const updateMournerHandler = async (values) => {
    const bodyData = {
      deceasedId: deceasedId,
      mournerId: selectedMourner?.mournerID,
      relation: values?.relation?.id,
      observance: values?.observance,
      first_adar_observance: values?.first_adar_observance,
      second_adar_observance: values?.second_adar_observance,
    };

    const token = await getAccessTokenSilently();

    setAddingMournerLoading(true);
    await AuthService.updateMourner(bodyData, token)
      .then((response) => {
        toastr.success("Success", "Mourner has been updated successfully");
        onAddSuccess();
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setAddingMournerLoading(false);
      });
  };

  const createMournerHandler = async (values) => {
    const bodyData = {
      id: deceasedId,
      relation: values?.relation?.id,
      observance: values?.observance,
      first_adar_observance: values?.first_adar_observance,
      second_adar_observance: values?.second_adar_observance,
      profile_img: values?.account?.profile_img,
    };

    if (!email) {
      bodyData.first_name = values?.account?.first_name;
      bodyData.last_name = values?.account?.last_name;
      bodyData.member_id = values?.account?._id;
    } else {
      bodyData.email = values?.email;
      bodyData.address = values?.address;
    }

    const token = await getAccessTokenSilently();

    setAddingMournerLoading(true);
    await AuthService.addMourner(bodyData, token)
      .then((response) => {
        toastr.success("Success", "Mourner has been added successfully");
        onAddSuccess();
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setAddingMournerLoading(false);
      });
  };

  return (
    <AddMournerContainer>
      <CloseButton onClick={closeModal}>
        <Close />
      </CloseButton>
      <div className="title">
        <Text className="sub-title">
          {type === "edit" ? "Edit Mourner" : "Add Mourners"}
        </Text>
        <p className="yah-note">Please complete the details below</p>
      </div>
      <Formik
        initialValues={{
          account: selectedMourner?.full_name
            ? {
                full_name: selectedMourner?.full_name,
              }
            : null,
          relation: getSelectedMournerRelation(),
          observance: selectedMourner?.observance || "english",
          first_adar_observance:
            selectedMourner?.first_adar_observance || false,
          second_adar_observance:
            selectedMourner?.second_adar_observance || false,
          email: "",
          address: "",
        }}
        validate={(values) => {
          const errors = {};
          if (!values.account && !email) {
            errors.account = "Required";
          }
          if (!values.relation) {
            errors.relation = "Required";
          }
          if (!values.address && email) {
            errors.address = "Required";
          }
          if ((!values.email || !emailValidation(values?.email)) && email) {
            errors.email = "Invalid email";
          }
          const findSelectedMourner = selectedMourners.find(
            (mourner) => mourner?.memberId === values?.account?._id
          );
          if (findSelectedMourner && values?.account?._id) {
            errors.account = "Mourner already added";
          }
          return errors;
        }}
        onSubmit={async (values, actions) => {
          if (addingMournerLoading) return;
          if (type === "edit") {
            await updateMournerHandler(values);
          } else {
            await createMournerHandler(values);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <div
            className={
              "flex-grow-1 d-flex flex-column justify-content-between mt-5"
            }
          >
            <div className="">
              <div className="row">
                <div className={"col-sm-12 mb-5"}>
                  {type === "edit" ? (
                    <InputField
                      {...{
                        title: "Select Mourner",
                        name: "mourner",
                        placeholder: "",
                        value: values?.account?.full_name,
                        disabled: true,
                      }}
                    />
                  ) : (
                    <>
                      <LabelField title="Select Mourner" />
                      <AsyncAutoCompleteField
                        {...{
                          name: "account",
                        }}
                        error={errors["account"]}
                        touched={touched["account"]}
                        onInputChange={(text) => {
                          searchAction(text);
                          setSearchSource("account");
                        }}
                        classNamePrefix="react-select"
                        options={contactsList}
                        isLoading={loading}
                        placeholder={"Type 3 letters to start searching"}
                        handleChange={(e) => {
                          // if (
                          //   values?.account?.label !== e?.label &&
                          //   isEdit === "true"
                          // )
                          //   setDisable(false);
                          localStorage.setItem("SelectedMourner", e.value);

                          setFieldValue("account", e);
                        }}
                        value={values.account}
                        onFocus={() => setEmail(null)}
                      />
                    </>
                  )}
                </div>
                <div className={`col-sm-12 ${!email && "pb-5"}`}>
                  <LabelField title="Mourner's Relationship to Deceased" />
                  <Select
                    // {...{
                    //   formik,
                    //   name: `memberName`,
                    //   checkValidation: false,
                    // }}
                    onInputChange={(text) => {
                      searchAction(text);
                      setSearchSource("relationship");
                    }}
                    classNamePrefix="react-select"
                    formatOptionLabel={formatOptionLabel}
                    options={deceasedRelationshipsOptions}
                    isLoading={loading}
                    styles={colourStyles}
                    onChange={(value) => {
                      setFieldValue(`relation`, {
                        id: value.value,
                        name: value.label,
                      });
                    }}
                    value={
                      values?.relation
                        ? {
                            label: values?.relation?.name,
                            value: values?.relation?.id,
                          }
                        : ""
                    }
                    components={{ DropdownIndicator }}
                    // isDisabled={index > 0}
                  />

                  {touched["relation"] && errors["relation"] ? (
                    <HintField error={errors["relation"]} />
                  ) : null}
                </div>
              </div>
              {email && (
                <div className="row pt-5">
                  <div className={"col-sm-12"}>
                    <InputField
                      {...{
                        handleChange,
                        title: "Email",
                        name: "email",
                        placeholder: "cohen22@gmail.com",
                        value: values?.email,
                        copy: true,
                      }}
                    />
                    {touched["email"] && errors["email"] ? (
                      <HintField error={errors["email"]} />
                    ) : null}
                  </div>
                  <div className={"col-sm-12 mt-5"}>
                    <InputField
                      {...{
                        handleChange,
                        title: "Address",
                        name: "address",
                        placeholder: "New York",
                        value: values?.address,
                      }}
                    />
                    {touched["address"] && errors["address"] ? (
                      <HintField error={errors["address"]} />
                    ) : null}
                  </div>
                </div>
              )}

              <div className={"pt-10"}>
                <LabelField title="Observance" />
                <MyRadioGroup
                  row
                  handleChange={(e) => {
                    setFieldValue("observance", e.target.value);
                  }}
                  value={values.observance}
                  name="observance"
                  className=""
                  formsControls={[
                    { value: "hebrew", label: "Hebrew date" },
                    { value: "english", label: "English date" },
                  ]}
                />
              </div>
              {values.observance == "english" ? (
                ""
              ) : (
                <div className="py-5">
                  <div className="d-flex h-20px">
                    <SwitchComp
                      checked={values?.first_adar_observance}
                      handleChange={(e) => {
                        if (values?.second_adar_observance) {
                          return;
                        }
                        setFieldValue(
                          "first_adar_observance",
                          e.target.checked
                        );
                      }}
                      className="mr-0"
                    />
                    <LabelField
                      title="1 Adar Observance"
                      style={{ alignSelf: "center" }}
                    />
                  </div>
                  <div className="d-flex ">
                    <SwitchComp
                      checked={values?.second_adar_observance}
                      handleChange={(e) => {
                        if (values?.first_adar_observance) {
                          return;
                        }
                        setFieldValue(
                          "second_adar_observance",
                          e.target.checked
                        );
                      }}
                      className="mr-0"
                    />
                    <LabelField
                      title="2 Adar Observance"
                      style={{ alignSelf: "center" }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <Button
                onClick={handleSubmit}
                size="md"
                variant="primary"
                disabled={addingMournerLoading}
                style={{ minWidth: "130px" }}
              >
                {addingMournerLoading ? (
                  <div className="d-flex align-items-center justify-content-center">
                    <CircularProgress size={14} />
                  </div>
                ) : type === "edit" ? (
                  "Update"
                ) : (
                  "Add Mourner"
                )}
              </Button>
            </div>
          </div>
        )}
      </Formik>
    </AddMournerContainer>
  );
};

export default AddMourner;
